import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Cloud, Lock, CheckCircle2, ArrowRight, Zap, Shield, Activity, Globe, Server, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Link } from 'react-router-dom';
import MultiStepLeadForm from '@/src/components/MultiStepLeadForm';

const CLOUD_RISKS = [
  "Insecure API endpoints and data leaks",
  "Misconfigured S3 buckets and storage",
  "Over-privileged IAM roles and permissions",
  "Lack of visibility across multi-cloud environments",
  "Inadequate cloud security monitoring",
  "Data loss and recovery issues",
  "Compliance and regulatory violations",
  "Insider threats and account hijacking"
];

export default function CloudSecurity() {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredRisks = CLOUD_RISKS.filter(risk => 
    risk.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="relative pt-32 pb-24 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,150,255,0.05),transparent_50%)]" />
        <div className="absolute top-0 left-0 w-full h-full cyber-grid opacity-10" />
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-20 items-start">
          {/* Left Side: Sales Copy */}
          <div className="space-y-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-accent text-[10px] font-bold uppercase tracking-[0.4em]"
            >
              <Cloud className="w-3 h-3" /> Cloud-Native Protection
            </motion.div>

            <div className="space-y-6">
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-5xl md:text-7xl font-display font-bold tracking-tighter leading-[0.9]"
              >
                Secure Your <br />
                <span className="text-accent glow-text">Cloud Infrastructure.</span>
              </motion.h1>
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-xl text-muted-foreground font-light max-w-xl leading-relaxed"
              >
                The cloud offers agility, but it also introduces new risks. We help you build a secure, resilient cloud environment that scales with your business.
              </motion.p>
            </div>

            {/* Pain Points */}
            <div className="space-y-8">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">The Cloud Risks We Mitigate:</h3>
                <div className="relative max-w-xs w-full">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input 
                    placeholder="Search risks..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 h-10 bg-white/5 border-white/10 rounded-xl text-xs focus:border-accent/50"
                  />
                </div>
              </div>
              
              <div className="grid gap-4">
                {filteredRisks.length > 0 ? (
                  filteredRisks.map((point, i) => (
                    <motion.div 
                      key={point}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="flex items-center gap-3 text-red-400/80"
                    >
                      <Shield className="w-5 h-5 shrink-0" />
                      <span className="text-sm font-medium">{point}</span>
                    </motion.div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground italic">No matching risks found.</p>
                )}
              </div>
            </div>

            {/* Benefits */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 rounded-2xl bg-white/5 border border-white/10 space-y-4">
                <Globe className="w-8 h-8 text-accent" />
                <h4 className="font-bold">Zero-Trust Architecture</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">Implement a zero-trust model to ensure that only authorized users and devices can access your data.</p>
              </div>
              <div className="p-6 rounded-2xl bg-white/5 border border-white/10 space-y-4">
                <Server className="w-8 h-8 text-accent" />
                <h4 className="font-bold">Automated Compliance</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">Continuously monitor your cloud environment for compliance with industry standards and regulations.</p>
              </div>
            </div>

            <div className="flex items-center gap-6 p-6 rounded-2xl bg-accent/5 border border-accent/20">
              <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center text-accent">
                <Zap className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm font-bold">Multi-Cloud Expertise</p>
                <p className="text-xs text-muted-foreground">Specialized security for AWS, Azure, and Google Cloud.</p>
              </div>
            </div>
          </div>

          {/* Right Side: Form */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="sticky top-32"
          >
            <div className="glass-card p-10 rounded-[3rem] border-white/10 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-accent/10 blur-[60px] rounded-full" />
              <div className="text-center mb-10 space-y-2 relative z-10">
                <h2 className="text-3xl font-display font-bold">Secure Your <span className="text-accent">Cloud</span></h2>
                <p className="text-sm text-muted-foreground font-light italic">Build fast, stay secure.</p>
              </div>
              <MultiStepLeadForm />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
